import { useAdvanceTargetingCampaignModalStore } from '@forwardflow/store/modal/AdvanceTargetingCampaignModal'
import { Modal, DatePicker, message } from 'antd'
import { useState, useRef, useEffect, useMemo } from 'react'
import { getFilterIcon, FilterType } from '../../utils/filterIcons'
import Icon from '@forwardflow/ui/Icons'
import {
   useGetFubStages,
   useGetFubTags,
} from '@forwardflow/hooks/customHooks/stageMapping'
import dayjs from 'dayjs'
import CampaignService from '@forwardflow/services/CampaignService'
import { CampaignData } from '@forwardflow/types'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button } from '@forwardflow/ui'
import { useGetFubSources } from '@forwardflow/hooks/customHooks/stageMapping/sourceList'
import { useGetFubPonds } from '@forwardflow/hooks/customHooks/stageMapping/fubPonds'
import {
   useGetActiveLeadSource,
   useGetActiveLeadSourceDetails,
} from '@forwardflow/hooks/customHooks'
import { CheckBoxField as CheckBox } from '@forwardflow/ui/CheckBoxField'

type TagFilterType = 'CONTAINS_ALL' | 'CONTAINS_ANY'
type AgeFilterType = '>=' | '<=' | '>' | '<'

interface FilterRow {
   id: string
   type: FilterType
   sources?: string[]
   stages?: string[]
   tags?: string[]
   tagFilterType?: 'CONTAINS_ALL' | 'CONTAINS_ANY'
   dateFilterType?: 'before' | 'after' | 'between'
   startDate?: string
   endDate?: string
   ageFilterType?: AgeFilterType
   ageDays?: string
   assignedPonds?: string[]
   includeOnlyUnassignedPondLeads?: boolean
}

export const AdvanceTargetingCampaignModal = () => {
   const navigate = useNavigate()
   const [searchParams] = useSearchParams()
   const isEditMode = searchParams.has('edit')
   const { title: leadSource } = useParams()
   const { data: activeLeadSourceData, mutate: mutateActiveLeadSource } =
      useGetActiveLeadSource(leadSource as string)
   const hasCampaignFiltersChanges =
      activeLeadSourceData?.draft?.changedSections?.includes('campaign_filters')
   const { data: campaignDetails, mutate: mutateActiveLeadSourceDetails } =
      useGetActiveLeadSourceDetails(leadSource as string)
   const {
      isAdvanceTargetingCampaignModalOpen,
      closeAdvanceTargetingCampaignModal,
   } = useAdvanceTargetingCampaignModalStore()
   const [campaignName, setCampaignName] = useState('')
   const [isLoading, setIsLoading] = useState(false)
   const [campaignNameError, setCampaignNameError] = useState(false)
   const [stageSearchQuery, setStageSearchQuery] = useState('')
   const [tagSearchQuery, setTagSearchQuery] = useState('')
   const [queryParams, setQueryParams] = useState({
      page: 1,
      limit: 20,
      search: '',
   })
   const [tagQueryParams, setTagQueryParams] = useState({
      page: 1,
      limit: 20,
      search: '',
   })
   const [isTagSearching, setIsTagSearching] = useState(false)
   const [isLoadingMoreTags, setIsLoadingMoreTags] = useState(false)
   const [accumulatedTags, setAccumulatedTags] = useState<any[]>([])
   const tagSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
   const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
   const [isSearching, setIsSearching] = useState(false)

   const [sourceSearchQuery, setSourceSearchQuery] = useState('')
   const [isLoadingMore, setIsLoadingMore] = useState(false)
   const [totalSources, setTotalSources] = useState(0)

   const { data: dataSource } = useGetFubSources(queryParams)
   const { data: stagesList } = useGetFubStages({
      page: 1,
      limit: 10000,
   })
   const { data: pondList } = useGetFubPonds({
      page: 1,
      limit: 10000,
      search: '',
   })
   const { data: tagsResponse } = useGetFubTags(tagQueryParams)

   useEffect(() => {
      if (
         dataSource &&
         dataSource?.meta &&
         dataSource?.meta?.totalItems > 0 &&
         queryParams.page === 1
      ) {
         setTotalSources(1000)
      }
   }, [dataSource, queryParams.page])

   const handleSourceDropdownScroll = (e: React.UIEvent<HTMLDivElement>) => {
      const bottom =
         e.currentTarget.scrollHeight - e.currentTarget.scrollTop ===
         e.currentTarget.clientHeight

      if (
         bottom &&
         !isLoadingMore &&
         dataSource &&
         dataSource?.meta &&
         dataSource?.meta?.totalItems < totalSources
      ) {
         setIsLoadingMore(true)

         setQueryParams((prev) => ({
            ...prev,
            page: prev.page + 1,
         }))
      }
   }

   useEffect(() => {
      if (isLoadingMore && dataSource) {
         setIsLoadingMore(false)
      }
   }, [dataSource, isLoadingMore])

   const [filterRows, setFilterRows] = useState<FilterRow[]>([
      { id: '1', type: 'source', sources: [] },
   ])

   const [openDropdowns, setOpenDropdowns] = useState<{
      [key: string]: boolean
   }>({})
   const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

   const filters = [
      { label: 'Source', value: 'source' },
      { label: 'Stages', value: 'stages' },
      { label: 'Tags', value: 'tags' },
      { label: 'Created', value: 'created' },
      { label: 'Age', value: 'age' },
      { label: 'Ponds', value: 'Ponds' },
   ]

   const [accumulatedSources, setAccumulatedSources] = useState<any[]>([])

   useEffect(() => {
      if (dataSource) {
         if (isLoadingMore) {
            setAccumulatedSources((prev) => [
               ...prev,
               ...(dataSource.data || []),
            ])
            setIsLoadingMore(false)
         } else if (queryParams.page === 1) {
            setAccumulatedSources(dataSource.data || [])
         }

         setIsSearching(false)
      }
   }, [dataSource, isLoadingMore, queryParams.page])

   const handleSourceSearch = (searchText: string) => {
      // Update local state immediately for UI feedback
      setSourceSearchQuery(searchText)

      // Clear any existing timeout
      if (searchTimeoutRef.current) {
         clearTimeout(searchTimeoutRef.current)
      }

      // For first character or empty results, search immediately
      const shouldSearchImmediately =
         searchText.length === 1 ||
         (searchText &&
            sourceOptions?.filter((option) =>
               option.label.toLowerCase().includes(searchText.toLowerCase())
            ).length === 0)

      const debounceTime = shouldSearchImmediately ? 0 : 300

      // Set a new timeout to update query params after typing stops
      searchTimeoutRef.current = setTimeout(() => {
         // Only update if the search term has changed
         if (searchText !== queryParams.search) {
            // Indicate that we're searching
            setIsSearching(true)

            // Reset accumulated sources for new search
            setAccumulatedSources([])

            // Reset page to 1 and update search term
            setQueryParams((prev) => ({
               ...prev,
               page: 1,
               search: searchText,
            }))
         }
      }, debounceTime)
   }

   const sourceOptions = accumulatedSources?.map((source) => ({
      label: source.campaignName || source.leadSource,
      value: source.campaignName || source.leadSource,
      count: source.totalLeads || 0,
   }))

   const filteredSourceOptions = useMemo(() => {
      if (!sourceSearchQuery) return sourceOptions
      const filtered =
         sourceOptions?.filter((option) =>
            option.label.toLowerCase().includes(sourceSearchQuery.toLowerCase())
         ) || []

      // If no results found in current list and not already searching, trigger a search
      if (filtered.length === 0 && !isSearching && sourceSearchQuery.trim()) {
         handleSourceSearch(sourceSearchQuery)
      }

      return filtered
   }, [sourceOptions, sourceSearchQuery, isSearching])

   const stageOptions =
      stagesList?.data?.map((stage: any, index) => ({
         label: stage?.stageName,
         value: stage?.stageName,
         count: index,
      })) || []

   const tagOptions =
      accumulatedTags?.map((tag, index) => ({
         label: tag.tagName,
         value: tag.tagName,
         count: index,
      })) || []

   const pondOptions =
      pondList?.map((pond: any) => ({
         label: pond.pondName,
         value: pond.pondId.toString(),
         count: pond.totalMembers || 0,
      })) || []

   const filteredStageOptions = useMemo(() => {
      if (!stageSearchQuery) return stageOptions
      return (
         stageOptions?.filter((option) =>
            option.label.toLowerCase().includes(stageSearchQuery.toLowerCase())
         ) || []
      )
   }, [stageOptions, stageSearchQuery])

   const filteredTagOptions = useMemo(() => {
      if (!tagSearchQuery) return tagOptions
      return (
         tagOptions?.filter((option) =>
            option.label.toLowerCase().includes(tagSearchQuery.toLowerCase())
         ) || []
      )
   }, [tagOptions, tagSearchQuery])

   useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
         Object.entries(dropdownRefs.current).forEach(([key, ref]) => {
            if (ref && !ref.contains(event.target as Node)) {
               setOpenDropdowns((prev) => ({ ...prev, [key]: false }))
            }
         })
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
   }, [])

   const toggleDropdown = (key: string) => {
      setOpenDropdowns((prev) => ({
         ...prev,
         [key]: !prev[key],
      }))
   }

   const handleTagDropdownScroll = (e: React.UIEvent<HTMLDivElement>) => {
      const bottom =
         e.currentTarget.scrollHeight - e.currentTarget.scrollTop ===
         e.currentTarget.clientHeight

      if (
         bottom &&
         !isLoadingMoreTags &&
         tagsResponse?.meta &&
         tagsResponse?.meta?.currentPage < tagsResponse?.meta?.totalPages
      ) {
         setIsLoadingMoreTags(true)

         setTagQueryParams((prev) => ({
            ...prev,
            page: prev.page + 1,
         }))
      }
   }

   useEffect(() => {
      if (tagsResponse?.data) {
         if (isLoadingMoreTags) {
            setAccumulatedTags((prev) => [...prev, ...tagsResponse.data])
            setIsLoadingMoreTags(false)
         } else if (tagQueryParams.page === 1) {
            setAccumulatedTags(tagsResponse.data)
         }
         setIsTagSearching(false)
      }
   }, [tagsResponse, isLoadingMoreTags, tagQueryParams.page])

   useEffect(() => {
      return () => {
         if (tagSearchTimeoutRef.current) {
            clearTimeout(tagSearchTimeoutRef.current)
         }
      }
   }, [])

   const changeFilterType = (rowId: string, type: FilterType) => {
      const isTypeAlreadyUsed = filterRows.some(
         (row) => row.id !== rowId && row.type === type
      )

      if (isTypeAlreadyUsed) {
         setOpenDropdowns((prev) => ({ ...prev, [`type-${rowId}`]: false }))
         return
      }

      const hasOtherCreatedOrAge = filterRows.some(
         (row) =>
            row.id !== rowId && (row.type === 'created' || row.type === 'age')
      )

      if ((type === 'created' || type === 'age') && hasOtherCreatedOrAge) {
         setOpenDropdowns((prev) => ({ ...prev, [`type-${rowId}`]: false }))
         return
      }

      setFilterRows((prev) =>
         prev.map((row) => {
            if (row.id === rowId) {
               return {
                  ...row,
                  type,
                  sources: type === 'source' ? [] : undefined,
                  stages: type === 'stages' ? [] : undefined,
                  tags: type === 'tags' ? [] : undefined,
                  tagFilterType: type === 'tags' ? 'CONTAINS_ALL' : undefined,
                  dateFilterType: type === 'created' ? 'before' : undefined,
                  startDate: type === 'created' ? '' : undefined,
                  endDate: type === 'created' ? '' : undefined,
                  ageFilterType: type === 'age' ? '>=' : undefined,
                  ageDays: type === 'age' ? '' : undefined,
                  assignedPonds: type === 'Ponds' ? [] : undefined,
               }
            }
            return row
         })
      )
      setOpenDropdowns((prev) => ({ ...prev, [`type-${rowId}`]: false }))
   }
   const validateCampaignName = () => {
      if (!campaignName.trim()) {
         setCampaignNameError(true)
         return false
      }
      setCampaignNameError(false)
      return true
   }
   const handleCampaignNameChange = (
      e: React.ChangeEvent<HTMLInputElement>
   ) => {
      setCampaignName(e.target.value)
      if (e.target.value.trim()) {
         setCampaignNameError(false)
      }
   }
   const addFilterRow = () => {
      const availableFilters = getAvailableFilters()

      // If all filter types are in use, don't add another row
      if (availableFilters.length === 0) {
         return
      }

      const newFilterType = availableFilters[0].value as FilterType
      const newId = String(Date.now())

      // Initialize the new row with appropriate defaults based on the filter type
      const newRow: FilterRow = { id: newId, type: newFilterType }

      // Add appropriate props based on filter type
      switch (newFilterType) {
         case 'source':
            newRow.sources = []
            break
         case 'stages':
            newRow.stages = []
            break
         case 'tags':
            newRow.tags = []
            newRow.tagFilterType = 'CONTAINS_ALL'
            break
         case 'created':
            newRow.dateFilterType = 'before'
            newRow.startDate = ''
            newRow.endDate = ''
            break
         case 'age':
            newRow.ageFilterType = '>='
            newRow.ageDays = ''
            break
         case 'Ponds':
            newRow.assignedPonds = []
            newRow.includeOnlyUnassignedPondLeads = false
            break
      }

      setFilterRows((prev) => [...prev, newRow])
   }

   // Remove a filter row
   const removeFilterRow = (rowId: string) => {
      setFilterRows((prev) => prev.filter((row) => row.id !== rowId))
   }

   // Source handlers
   const handleSourceToggle = (rowId: string, value: string) => {
      setFilterRows((prev) =>
         prev.map((row) => {
            if (row.id === rowId && row.sources) {
               const sources = row.sources.includes(value)
                  ? row.sources.filter((item) => item !== value)
                  : [...row.sources, value]
               return { ...row, sources }
            }
            return row
         })
      )
   }

   const handleSourceSelectAll = (rowId: string) => {
      setFilterRows((prev) =>
         prev.map((row) => {
            if (row.id === rowId && row.sources) {
               // Check if all sources are already selected
               if (row.sources.length === sourceOptions?.length) {
                  // If all are selected, deselect all
                  return { ...row, sources: [] }
               } else {
                  // Otherwise, select ALL sources regardless of filtering
                  return {
                     ...row,
                     sources: sourceOptions?.map((o) => o.value) || [],
                  }
               }
            }
            return row
         })
      )
   }

   // Clear search when dropdown closes
   useEffect(() => {
      const anySourceDropdownOpen = Object.entries(openDropdowns).some(
         ([key, isOpen]) => key.startsWith('source-') && isOpen
      )

      const anyStageDropdownOpen = Object.entries(openDropdowns).some(
         ([key, isOpen]) => key.startsWith('stages-') && isOpen
      )

      const anyTagDropdownOpen = Object.entries(openDropdowns).some(
         ([key, isOpen]) => key.startsWith('tags-') && isOpen
      )

      if (!anySourceDropdownOpen) {
         setSourceSearchQuery('')
      }

      if (!anyStageDropdownOpen) {
         setStageSearchQuery('')
      }

      if (!anyTagDropdownOpen) {
         setTagSearchQuery('')
      }
   }, [openDropdowns])

   // Stage handlers
   const handleStageToggle = (rowId: string, value: string) => {
      setFilterRows((prev) =>
         prev.map((row) => {
            if (row.id === rowId && row.stages) {
               const stages = row.stages.includes(value)
                  ? row.stages.filter((item) => item !== value)
                  : [...row.stages, value]
               return { ...row, stages }
            }
            return row
         })
      )
   }

   // For stages
   const handleStageSelectAll = (rowId: string) => {
      setFilterRows((prev) =>
         prev.map((row) => {
            if (row.id === rowId && row.stages) {
               // Check if all stages are already selected
               if (row.stages.length === stageOptions?.length) {
                  // If all are selected, deselect all
                  return { ...row, stages: [] }
               } else {
                  // Otherwise, select ALL stages regardless of filtering
                  return {
                     ...row,
                     stages: stageOptions?.map((o) => o.value) || [],
                  }
               }
            }
            return row
         })
      )
   }

   // Tag handlers
   const handleTagToggle = (rowId: string, value: string) => {
      setFilterRows((prev) =>
         prev.map((row) => {
            if (row.id === rowId && row.tags) {
               const tags = row.tags.includes(value)
                  ? row.tags.filter((item) => item !== value)
                  : [...row.tags, value]
               return { ...row, tags }
            }
            return row
         })
      )
   }

   // For tags
   const handleTagSelectAll = (rowId: string) => {
      setFilterRows((prev) =>
         prev.map((row) => {
            if (row.id === rowId && row.tags) {
               // Check if all tags are already selected
               if (row.tags.length === tagOptions?.length) {
                  // If all are selected, deselect all
                  return { ...row, tags: [] }
               } else {
                  // Otherwise, select ALL tags regardless of filtering
                  return {
                     ...row,
                     tags: tagOptions?.map((o) => o.value) || [],
                  }
               }
            }
            return row
         })
      )
   }

   const handleSetTagFilterType = (
      rowId: string,
      type: 'CONTAINS_ALL' | 'CONTAINS_ANY'
   ) => {
      setFilterRows((prev) =>
         prev.map((row) => {
            if (row.id === rowId) {
               return { ...row, tagFilterType: type }
            }
            return row
         })
      )
      setOpenDropdowns((prev) => ({ ...prev, [`tag-type-${rowId}`]: false }))
   }

   // Date handlers
   const handleSetDateFilterType = (
      rowId: string,
      type: 'before' | 'after' | 'between'
   ) => {
      setFilterRows((prev) =>
         prev.map((row) => {
            if (row.id === rowId) {
               return { ...row, dateFilterType: type }
            }
            return row
         })
      )
      setOpenDropdowns((prev) => ({ ...prev, [`date-type-${rowId}`]: false }))
   }

   const handleDateChange = (
      rowId: string,
      date: string,
      isEndDate = false
   ) => {
      setFilterRows((prev) =>
         prev.map((row) => {
            if (row.id === rowId) {
               if (isEndDate) {
                  return { ...row, endDate: date }
               } else {
                  return { ...row, startDate: date }
               }
            }
            return row
         })
      )
   }

   // Update the display text function for tag filter type
   const getTagFilterTypeText = (type?: TagFilterType) => {
      switch (type) {
         case 'CONTAINS_ALL':
            return 'Including all of'
         case 'CONTAINS_ANY':
            return 'Including any of'
         default:
            return 'CONTAINS_ALL'
      }
   }

   // Handler for age filter type dropdown
   const handleSetAgeFilterType = (rowId: string, type: AgeFilterType) => {
      setFilterRows((prev) =>
         prev.map((row) =>
            row.id === rowId ? { ...row, ageFilterType: type } : row
         )
      )
      setOpenDropdowns((prev) => ({ ...prev, [`age-type-${rowId}`]: false }))
   }

   const clearDateFilter = (rowId: string) => {
      setFilterRows((prev) =>
         prev.map((row) => {
            if (row.id === rowId) {
               return { ...row, startDate: '', endDate: '' }
            }
            return row
         })
      )
   }

   const getAgeFilterTypeText = (type?: AgeFilterType) => {
      switch (type) {
         case '>=':
            return 'Greater than'
         case '<=':
            return 'Less than'
         default:
            return 'Greater than'
      }
   }
   // Debounced search handler
   const handleStageSearch = (searchText: string) => {
      setStageSearchQuery(searchText)
   }
   // Clear age filter function
   const clearAgeFilter = (rowId: string) => {
      setFilterRows((prev) =>
         prev.map((row) => {
            if (row.id === rowId) {
               return { ...row, ageDays: '' }
            }
            return row
         })
      )
   }

   // Debounced search handler for tags
   const handleTagSearch = (searchText: string) => {
      // Update local state immediately for UI feedback
      setTagSearchQuery(searchText)

      // Clear any existing timeout
      if (tagSearchTimeoutRef.current) {
         clearTimeout(tagSearchTimeoutRef.current)
      }

      // Set a new timeout to update query params after typing stops
      tagSearchTimeoutRef.current = setTimeout(() => {
         // Only update if the search term has changed
         if (searchText !== tagQueryParams.search) {
            // Indicate that we're searching
            setIsTagSearching(true)

            // Reset accumulated tags for new search
            setAccumulatedTags([])

            // Reset page to 1 and update search term
            setTagQueryParams((prev) => ({
               ...prev,
               page: 1,
               search: searchText,
            }))
         }
      }, 300) // 300ms debounce time
   }

   // Add useEffect to populate fields when in edit mode
   useEffect(() => {
      if (isEditMode) {
         const dataSource = hasCampaignFiltersChanges
            ? campaignDetails
            : activeLeadSourceData

         if (dataSource) {
            setCampaignName(
               dataSource.campaignName || searchParams.get('campaignName') || ''
            )

            if (
               dataSource?.data?.campaignFilters?.eligibleCriteria &&
               Array.isArray(
                  dataSource?.data?.campaignFilters?.eligibleCriteria
               )
            ) {
               const tempFilterRows: FilterRow[] = []
               const pondLeadCriteria: any[] = []

               dataSource?.data?.campaignFilters?.eligibleCriteria.forEach(
                  (criteria: any, index: number) => {
                     if (criteria.field === 'pond_lead') {
                        pondLeadCriteria.push(criteria)
                        return
                     }

                     const row: FilterRow = {
                        id: String(Date.now() + index),
                        type: criteria.field as FilterType,
                     }

                     switch (criteria.field) {
                        case 'source':
                           row.sources = Array.isArray(criteria.value)
                              ? criteria.value
                              : [criteria.value]
                           break
                        case 'stage':
                           row.type = 'stages'

                           row.stages = Array.isArray(criteria.value)
                              ? criteria.value
                              : [criteria.value]
                           break
                        case 'tags':
                           row.tags = Array.isArray(criteria.value)
                              ? criteria.value
                              : [criteria.value]
                           row.tagFilterType =
                              (criteria.operator as
                                 | 'CONTAINS_ALL'
                                 | 'CONTAINS_ANY') || 'CONTAINS_ALL'
                           break
                        case 'created_at':
                           row.type = 'created'
                           if (criteria.operator === '<=') {
                              row.dateFilterType = 'before'
                              row.startDate = criteria.value
                           } else if (criteria.operator === 'BEFORE') {
                              row.dateFilterType = 'before'
                              row.startDate = criteria.value
                           } else if (criteria.operator === '>=') {
                              row.dateFilterType = 'after'
                              row.startDate = criteria.value
                           } else if (criteria.operator === 'AFTER') {
                              row.dateFilterType = 'after'
                              row.startDate = criteria.value
                           } else if (
                              criteria.operator === 'BETWEEN' &&
                              Array.isArray(criteria.value)
                           ) {
                              row.dateFilterType = 'between'
                              row.startDate = criteria.value[0]
                              row.endDate = criteria.value[1]
                           }
                           break
                        case 'age':
                           row.ageFilterType =
                              (criteria.operator as AgeFilterType) || '>='
                           row.ageDays = criteria.value?.toString() || ''
                           break
                        case 'assigned_pond_id':
                           row.type = 'Ponds'
                           row.assignedPonds = criteria.value?.map(String) || []
                           row.includeOnlyUnassignedPondLeads =
                              criteria.includeUnassigned || false
                           break
                     }

                     tempFilterRows.push(row)
                  }
               )

               pondLeadCriteria.forEach((criteria) => {
                  const pondsRowIndex = tempFilterRows.findIndex(
                     (r) => r.type === 'Ponds'
                  )
                  if (pondsRowIndex !== -1) {
                     tempFilterRows[
                        pondsRowIndex
                     ].includeOnlyUnassignedPondLeads = criteria.value === true
                  }
               })

               if (tempFilterRows.length > 0) {
                  setFilterRows(tempFilterRows)
               }
            }
         }
      }
   }, [
      isEditMode,
      activeLeadSourceData,
      campaignDetails,
      hasCampaignFiltersChanges,
   ])

   const prepareCampaignData = (): CampaignData => {
      const campaignData = {
         campaignName: campaignName || undefined,
         filters: filterRows
            .map((row) => {
               switch (row.type) {
                  case 'source':
                     if (row.sources && row.sources.length > 0) {
                        return {
                           type: 'source',
                           sources: row.sources,
                           field: 'source',
                           logic: 'AND',
                           value: row.sources,
                           operator: 'CONTAINS_ANY',
                        }
                     }
                     return null
                  case 'stages':
                     if (row.stages && row.stages.length > 0) {
                        return {
                           type: 'stages',
                           stages: row.stages,
                           field: 'stages',
                           logic: 'AND',
                           value: row.stages,
                           operator: 'CONTAINS_ANY',
                        }
                     }
                     return null
                  case 'tags':
                     if (row.tags && row.tags.length > 0) {
                        return {
                           type: 'tags',
                           tags: row.tags,
                           tagFilterType: row.tagFilterType,
                           field: 'tags',
                           logic: 'AND',
                           value: row.tags,
                           operator: row.tagFilterType || 'CONTAINS_ALL',
                        }
                     }
                     return null
                  case 'created':
                     if (row.startDate) {
                        const dateFilter: any = {
                           type: 'created',
                           dateFilterType: row.dateFilterType,
                           startDate: row.startDate,
                           endDate: row.endDate,
                           field: 'created',
                           logic: 'AND',
                        }

                        if (row.dateFilterType === 'before') {
                           dateFilter.operator = 'BEFORE'
                           dateFilter.value = row.startDate
                        } else if (row.dateFilterType === 'after') {
                           dateFilter.operator = 'AFTER'
                           dateFilter.value = row.startDate
                        } else if (
                           row.dateFilterType === 'between' &&
                           row.endDate
                        ) {
                           dateFilter.operator = 'BETWEEN'
                           dateFilter.value = [row.startDate, row.endDate]
                        }

                        return dateFilter
                     }
                     return null
                  case 'age':
                     if (row.ageDays && row.ageDays !== '') {
                        return {
                           type: 'age',
                           ageFilterType: row.ageFilterType,
                           ageDays: row.ageDays,
                           field: 'age',
                           logic: 'AND',
                           operator: row.ageFilterType || '>=',
                           value: parseInt(row.ageDays),
                        }
                     }
                     return null
                  case 'Ponds':
                     if (row.assignedPonds && row.assignedPonds.length > 0) {
                        return {
                           type: 'Ponds',
                           assignedPonds: row.assignedPonds,
                           includeOnlyUnassignedPondLeads:
                              row.includeOnlyUnassignedPondLeads,
                           field: 'Ponds',
                           logic: 'AND',
                           value: row.assignedPonds,
                           operator: 'CONTAINS_ANY',
                           includeUnassigned:
                              row.includeOnlyUnassignedPondLeads,
                        }
                     }
                     return null
                  default:
                     return null
               }
            })
            .filter((filter) => filter !== null) as any,
      }

      if (!campaignData.campaignName) {
         delete campaignData.campaignName
      }

      return campaignData
   }

   // Handler for ageDays input change
   const handleAgeDaysChange = (rowId: string, value: string) => {
      setFilterRows((prev) =>
         prev.map((row) =>
            row.id === rowId ? { ...row, ageDays: value } : row
         )
      )
   }

   // Assigned Pond handlers
   const handleAssignedPondToggle = (rowId: string, value: string) => {
      setFilterRows((prev) =>
         prev.map((row) => {
            if (row.id === rowId && row.assignedPonds) {
               const assignedPonds = row.assignedPonds.includes(value)
                  ? row.assignedPonds.filter((item) => item !== value)
                  : [...row.assignedPonds, value]
               return { ...row, assignedPonds }
            }
            return row
         })
      )
   }

   const handleUnassignedPondLeadsToggle = (rowId: string) => {
      setFilterRows((prev) =>
         prev.map((row) => {
            if (row.id === rowId) {
               return {
                  ...row,
                  includeOnlyUnassignedPondLeads:
                     !row.includeOnlyUnassignedPondLeads,
               }
            }
            return row
         })
      )
   }
   const handleAssignedPondSelectAll = (rowId: string) => {
      setFilterRows((prev) =>
         prev.map((row) => {
            if (row.id === rowId && row.assignedPonds) {
               // Check if all ponds are already selected
               if (row.assignedPonds.length === pondOptions?.length) {
                  // If all are selected, deselect all
                  return { ...row, assignedPonds: [] }
               } else {
                  // Otherwise, select ALL ponds
                  return {
                     ...row,
                     assignedPonds: pondOptions?.map((o) => o.value) || [],
                  }
               }
            }
            return row
         })
      )
   }

   // Helper to display selected stages as text
   const getSelectedStagesText = (selectedStages: string[]) => {
      if (!selectedStages.length) return 'Select stages'
      if (selectedStages.length === 1) return selectedStages[0]
      return `${selectedStages.length} stages selected`
   }

   // Helper to display selected tags as text
   const getSelectedTagsText = (selectedTags: string[]) => {
      if (!selectedTags.length) return 'Select tags'
      if (selectedTags.length === 1) return selectedTags[0]
      return `${selectedTags.length} tags selected`
   }

   // Helper to display selected sources as text
   const getSelectedSourcesText = (selectedSources: string[]) => {
      if (!selectedSources.length) return 'Select sources'
      if (selectedSources.length === 1) return selectedSources[0]
      return `${selectedSources.length} sources selected`
   }

   // Helper to display selected assigned ponds as text
   const getSelectedAssignedPondsText = (selectedAssignedPonds: string[]) => {
      if (!selectedAssignedPonds.length) return 'Select ponds'
      if (selectedAssignedPonds.length === 1) return selectedAssignedPonds[0]
      return `${selectedAssignedPonds.length} ponds selected`
   }

   // Render the filter control based on type
   const renderFilterControl = (row: FilterRow) => {
      switch (row.type) {
         case 'source':
            return (
               <>
                  {/* Source Multi-select Dropdown */}
                  <div
                     className="relative flex-1"
                     ref={(el) =>
                        (dropdownRefs.current[`source-${row.id}`] = el)
                     }
                  >
                     <div
                        className="bg-[#2a2a2a] text-white px-4 py-2 rounded-md focus:outline-none cursor-pointer flex items-center h-[62px]"
                        onClick={() => toggleDropdown(`source-${row.id}`)}
                     >
                        <span
                           className={`flex-1 ${
                              !row.sources?.length
                                 ? 'text-gray-400'
                                 : 'text-white'
                           }`}
                        >
                           {getSelectedSourcesText(row.sources || [])}
                        </span>
                        <Icon name="arrow-down" className="fill-white" />
                        {(row.sources?.length || 0) > 0 && (
                           <span className="flex items-center justify-center w-5 h-5 ml-2 text-xs bg-purple-600 rounded-full">
                              {row.sources?.length}
                           </span>
                        )}
                     </div>

                     {openDropdowns[`source-${row.id}`] && (
                        <div
                           className="absolute mt-1 w-full bg-[#2a2a2a] border border-gray-700 rounded-md z-10 shadow-lg max-h-[300px] overflow-y-auto"
                           onScroll={handleSourceDropdownScroll}
                        >
                           {/* Search input */}
                           <div className="sticky top-0 bg-[#2a2a2a] p-2 border-b border-gray-700">
                              <div className="relative">
                                 <input
                                    type="text"
                                    className="w-full bg-[#333] text-white px-3 py-2 pl-8 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
                                    placeholder="Search sources..."
                                    onChange={(e) =>
                                       handleSourceSearch(e.target.value)
                                    }
                                    value={sourceSearchQuery || ''}
                                    onClick={(e) => e.stopPropagation()}
                                 />

                                 <Icon
                                    name="search"
                                    className="absolute w-2 h-2 fill-gray-400 scale-75 left-1.5 top-[8px]"
                                    width={12}
                                    height={12}
                                 />
                              </div>
                           </div>

                           <div
                              className="flex items-center px-4 py-2 border-b border-gray-700 cursor-pointer hover:bg-[#333] sticky top-[52px] bg-[#2a2a2a]"
                              onClick={() => handleSourceSelectAll(row.id)}
                           >
                              <CheckBox
                                 checked={
                                    row.sources?.length ===
                                    sourceOptions?.length
                                 }
                                 onChange={() => handleSourceSelectAll(row.id)}
                                 className="mr-3"
                              />
                              <span>Select all</span>
                              <span className="ml-auto text-gray-400">
                                 Total leads
                              </span>
                           </div>

                           <div className="overflow-y-auto">
                              {filteredSourceOptions?.map((option) => (
                                 <div
                                    key={option.value}
                                    className="flex items-center px-4 py-2 cursor-pointer hover:bg-[#333]"
                                    onClick={() =>
                                       handleSourceToggle(row.id, option.value)
                                    }
                                 >
                                    <CheckBox
                                       checked={
                                          row.sources?.includes(option.value) ||
                                          false
                                       }
                                       onChange={() =>
                                          handleSourceToggle(
                                             row.id,
                                             option.value
                                          )
                                       }
                                       className="mr-3"
                                    />
                                    <span>{option?.label}</span>
                                    <span className="ml-auto text-gray-400">
                                       {option?.count?.toLocaleString()}
                                    </span>
                                 </div>
                              ))}

                              {/* Loading indicator */}
                              {isLoadingMore && (
                                 <div className="flex items-center justify-center py-2 text-gray-400">
                                    Loading more...
                                 </div>
                              )}

                              {/* No results message */}
                              {filteredSourceOptions?.length === 0 &&
                                 !isLoadingMore &&
                                 !isSearching && (
                                    <div className="py-4 text-center text-gray-400">
                                       No sources match your search.
                                    </div>
                                 )}

                              {isSearching && (
                                 <div className="flex items-center justify-center p-2 text-gray-400 bg-[#2a2a2a] sticky top-[52px] border-b border-gray-700">
                                    Searching...
                                 </div>
                              )}
                           </div>
                        </div>
                     )}
                  </div>
               </>
            )

         case 'stages':
            return (
               <div
                  className="relative flex-1"
                  ref={(el) => (dropdownRefs.current[`stages-${row.id}`] = el)}
               >
                  <div
                     className="bg-[#2a2a2a] text-white px-4 py-2 rounded-md focus:outline-none cursor-pointer flex items-center h-[62px]"
                     onClick={() => toggleDropdown(`stages-${row.id}`)}
                  >
                     <span
                        className={`flex-1 ${
                           !row.stages?.length ? 'text-gray-400' : 'text-white'
                        }`}
                     >
                        {getSelectedStagesText(row.stages || [])}
                     </span>
                     <Icon name="arrow-down" className="fill-white" />
                     {(row.stages?.length || 0) > 0 && (
                        <span className="flex items-center justify-center w-5 h-5 ml-2 text-xs bg-purple-600 rounded-full">
                           {row.stages?.length}
                        </span>
                     )}
                  </div>

                  {openDropdowns[`stages-${row.id}`] && (
                     <div className="absolute mt-1 w-full bg-[#2a2a2a] border border-gray-700 rounded-md z-10 shadow-lg max-h-[300px] overflow-auto">
                        {/* Search input for stages */}
                        <div className="sticky top-0 bg-[#2a2a2a] p-2 border-b border-gray-700">
                           <div className="relative">
                              <input
                                 type="text"
                                 className="w-full bg-[#333] text-white px-3 py-2 pl-8 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
                                 placeholder="Search stages..."
                                 onChange={(e) =>
                                    handleStageSearch(e.target.value)
                                 }
                                 value={stageSearchQuery || ''}
                                 onClick={(e) => e.stopPropagation()}
                              />
                              <Icon
                                 name="search"
                                 className="absolute w-2 h-2 fill-gray-400 scale-75 left-1.5 top-[8px]"
                                 width={12}
                                 height={12}
                              />
                           </div>
                        </div>

                        <div
                           className="flex items-center px-4 py-2 border-b border-gray-700 cursor-pointer hover:bg-[#333] sticky top-[52px] bg-[#2a2a2a]"
                           onClick={() => handleStageSelectAll(row.id)}
                        >
                           <CheckBox
                              checked={
                                 row.stages?.length === stageOptions.length
                              }
                              onChange={() => handleStageSelectAll(row.id)}
                              className="mr-3"
                           />
                           <span>Select all</span>
                           <span className="ml-3 text-gray-400">Stages</span>
                        </div>

                        {filteredStageOptions.map((option) => (
                           <div
                              key={option.value}
                              className="flex items-center px-4 py-2 cursor-pointer hover:bg-[#333]"
                              onClick={() =>
                                 handleStageToggle(row.id, option.value)
                              }
                           >
                              <CheckBox
                                 checked={
                                    row.stages?.includes(option.value) || false
                                 }
                                 onChange={() =>
                                    handleStageToggle(row.id, option.value)
                                 }
                                 className="mr-3"
                              />
                              <span>{option.label}</span>
                           </div>
                        ))}

                        {/* No results message */}
                        {filteredStageOptions.length === 0 && (
                           <div className="py-4 text-center text-gray-400">
                              No stages match your search.
                           </div>
                        )}
                     </div>
                  )}
               </div>
            )

         case 'tags':
            return (
               <>
                  <div
                     className="relative flex-1 max-w-52"
                     ref={(el) =>
                        (dropdownRefs.current[`tag-type-${row.id}`] = el)
                     }
                  >
                     <div
                        className="bg-[#2a2a2a] text-white px-4 py-2 rounded-md focus:outline-none cursor-pointer flex items-center h-[62px]"
                        onClick={() => toggleDropdown(`tag-type-${row.id}`)}
                     >
                        <span className="flex-1 text-white">
                           {getTagFilterTypeText(row.tagFilterType)}
                        </span>
                        <Icon name="arrow-down" className="fill-white" />
                     </div>

                     {openDropdowns[`tag-type-${row.id}`] && (
                        <div className="absolute mt-1 w-full bg-[#2a2a2a] border border-gray-700 rounded-md z-10 shadow-lg">
                           <div
                              className="px-4 py-2 cursor-pointer hover:bg-[#333] text-white"
                              onClick={() =>
                                 handleSetTagFilterType(row.id, 'CONTAINS_ALL')
                              }
                           >
                              Including all of
                           </div>
                           <div
                              className="px-4 py-2 cursor-pointer hover:bg-[#333] text-white"
                              onClick={() =>
                                 handleSetTagFilterType(row.id, 'CONTAINS_ANY')
                              }
                           >
                              Including any of
                           </div>
                        </div>
                     )}
                  </div>

                  {/* Tags Multi-select Dropdown */}
                  <div
                     className="relative flex-1"
                     ref={(el) => (dropdownRefs.current[`tags-${row.id}`] = el)}
                  >
                     <div
                        className="bg-[#2a2a2a] text-white px-4 py-2 rounded-md focus:outline-none cursor-pointer flex items-center h-[62px]"
                        onClick={() => toggleDropdown(`tags-${row.id}`)}
                     >
                        <span
                           className={`flex-1 ${
                              !row.tags?.length ? 'text-gray-400' : 'text-white'
                           }`}
                        >
                           {getSelectedTagsText(row.tags || [])}
                        </span>
                        <svg
                           className="w-4 h-4 ml-2"
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24"
                           xmlns="http://www.w3.org/2000/svg"
                        >
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                           />
                        </svg>
                        {(row.tags?.length || 0) > 0 && (
                           <span className="flex items-center justify-center w-5 h-5 ml-2 text-xs bg-purple-600 rounded-full">
                              {row.tags?.length}
                           </span>
                        )}
                     </div>

                     {openDropdowns[`tags-${row.id}`] && (
                        <div
                           className="absolute mt-1 w-full bg-[#2a2a2a] border border-gray-700 rounded-md z-10 shadow-lg max-h-[300px] overflow-auto"
                           onScroll={handleTagDropdownScroll}
                        >
                           {/* Search input for tags */}
                           <div className="sticky top-0 bg-[#2a2a2a] p-2 border-b border-gray-700">
                              <div className="relative">
                                 <input
                                    type="text"
                                    className="w-full bg-[#333] text-white px-3 py-2 pl-8 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
                                    placeholder="Search tags..."
                                    onChange={(e) =>
                                       handleTagSearch(e.target.value)
                                    }
                                    value={tagSearchQuery || ''}
                                    onClick={(e) => e.stopPropagation()}
                                 />
                                 <Icon
                                    name="search"
                                    className="absolute w-2 h-2 fill-gray-400 scale-75 left-1.5 top-[8px]"
                                    width={12}
                                    height={12}
                                 />
                              </div>
                           </div>

                           <div
                              className="flex items-center px-4 py-2 border-b border-gray-700 cursor-pointer hover:bg-[#333] sticky top-[52px] bg-[#2a2a2a]"
                              onClick={() => handleTagSelectAll(row.id)}
                           >
                              <CheckBox
                                 checked={
                                    row.tags?.length === tagOptions?.length
                                 }
                                 onChange={() => handleTagSelectAll(row.id)}
                                 className="mr-3"
                              />
                              <span>Select all</span>
                              <span className="ml-3 text-gray-400">Tags</span>
                           </div>

                           <div className="overflow-y-auto">
                              {filteredTagOptions?.map((option) => (
                                 <div
                                    key={option.value}
                                    className="flex items-center px-4 py-2 cursor-pointer hover:bg-[#333]"
                                    onClick={() =>
                                       handleTagToggle(row.id, option.value)
                                    }
                                 >
                                    <CheckBox
                                       checked={
                                          row.tags?.includes(option.value) ||
                                          false
                                       }
                                       onChange={() =>
                                          handleTagToggle(row.id, option.value)
                                       }
                                       className="mr-3"
                                    />
                                    <span>{option.label}</span>
                                 </div>
                              ))}

                              {/* Loading indicator */}
                              {isLoadingMoreTags && (
                                 <div className="flex items-center justify-center py-2 text-gray-400">
                                    Loading more...
                                 </div>
                              )}

                              {/* No results message */}
                              {filteredTagOptions?.length === 0 &&
                                 !isLoadingMoreTags &&
                                 !isTagSearching && (
                                    <div className="py-4 text-center text-gray-400">
                                       No tags match your search.
                                    </div>
                                 )}

                              {isTagSearching && (
                                 <div className="flex items-center justify-center p-2 text-gray-400 bg-[#2a2a2a] sticky top-[52px] border-b border-gray-700">
                                    Searching...
                                 </div>
                              )}
                           </div>
                        </div>
                     )}
                  </div>
               </>
            )

         case 'created':
            return (
               <>
                  <div
                     className="relative flex-1 max-w-52"
                     ref={(el) =>
                        (dropdownRefs.current[`date-type-${row.id}`] = el)
                     }
                  >
                     <div
                        className="bg-[#2a2a2a] text-white px-4 py-2 rounded-md focus:outline-none cursor-pointer flex items-center h-[62px]"
                        onClick={() => toggleDropdown(`date-type-${row.id}`)}
                     >
                        <span className="flex-1 text-white">
                           {row.dateFilterType === 'before'
                              ? 'Before'
                              : row.dateFilterType === 'after'
                              ? 'After'
                              : 'Between'}
                        </span>
                        <Icon name="arrow-down" className="fill-white" />
                     </div>

                     {openDropdowns[`date-type-${row.id}`] && (
                        <div className="absolute mt-1 w-full bg-[#2a2a2a] border border-gray-700 rounded-md z-10 shadow-lg">
                           <div
                              className="px-4 py-2 cursor-pointer hover:bg-[#333] text-white"
                              onClick={() =>
                                 handleSetDateFilterType(row.id, 'before')
                              }
                           >
                              Before
                           </div>
                           <div
                              className="px-4 py-2 cursor-pointer hover:bg-[#333] text-white"
                              onClick={() =>
                                 handleSetDateFilterType(row.id, 'after')
                              }
                           >
                              After
                           </div>
                           <div
                              className="px-4 py-2 cursor-pointer hover:bg-[#333] text-white"
                              onClick={() =>
                                 handleSetDateFilterType(row.id, 'between')
                              }
                           >
                              Between
                           </div>
                        </div>
                     )}
                  </div>

                  <div className="flex flex-1 gap-2 ff-datepicker">
                     <DatePicker
                        className="bg-[#2a2a2a] text-white w-full h-[62px] rounded-md ff-datepicker"
                        placeholder="Select date"
                        value={row.startDate ? dayjs(row.startDate) : null}
                        onChange={(date) =>
                           handleDateChange(
                              row.id,
                              date ? date.format('YYYY-MM-DD') : ''
                           )
                        }
                        allowClear={false}
                     />

                     {row.dateFilterType === 'between' && (
                        <DatePicker
                           className="bg-[#2a2a2a] text-white w-full h-[62px] rounded-md"
                           placeholder="End date"
                           value={row.endDate ? dayjs(row.endDate) : null}
                           onChange={(date) =>
                              handleDateChange(
                                 row.id,
                                 date ? date.format('YYYY-MM-DD') : '',
                                 true
                              )
                           }
                           allowClear={false}
                        />
                     )}
                  </div>
               </>
            )

         case 'age':
            return (
               <>
                  <div
                     className="relative flex-1 max-w-52"
                     ref={(el) =>
                        (dropdownRefs.current[`age-type-${row.id}`] = el)
                     }
                  >
                     <div
                        className="bg-[#2a2a2a] text-white px-4 py-2 rounded-md focus:outline-none cursor-pointer flex items-center h-[62px]"
                        onClick={() => toggleDropdown(`age-type-${row.id}`)}
                     >
                        <span className="flex-1 text-white">
                           {getAgeFilterTypeText(row.ageFilterType)}
                        </span>
                        <Icon name="arrow-down" className="fill-white" />
                     </div>

                     {openDropdowns[`age-type-${row.id}`] && (
                        <div className="absolute mt-1 w-full bg-[#2a2a2a] border border-gray-700 rounded-md z-10 shadow-lg">
                           <div
                              className="px-4 py-2 cursor-pointer hover:bg-[#333] text-white"
                              onClick={() =>
                                 handleSetAgeFilterType(row.id, '>=')
                              }
                           >
                              Greater than
                           </div>
                           <div
                              className="px-4 py-2 cursor-pointer hover:bg-[#333] text-white"
                              onClick={() =>
                                 handleSetAgeFilterType(row.id, '<=')
                              }
                           >
                              Less than
                           </div>
                        </div>
                     )}
                  </div>

                  <div className="flex-1">
                     <input
                        type="text"
                        className="w-full h-[62px] bg-[#2a2a2a] text-white px-4 py-2 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500"
                        placeholder="Enter days"
                        value={row.ageDays || ''}
                        onChange={(e) => {
                           const value = e.target.value.replace(/[^0-9]/g, '')
                           handleAgeDaysChange(row.id, value)
                        }}
                        onClick={(e) => e.stopPropagation()}
                     />
                  </div>
               </>
            )

         case 'Ponds':
            return (
               <div className="flex-1">
                  <div
                     className="relative"
                     ref={(el) =>
                        (dropdownRefs.current[`assigned-pond-${row.id}`] = el)
                     }
                  >
                     <div
                        className="bg-[#2a2a2a] text-white px-4 py-2 rounded-md focus:outline-none cursor-pointer flex items-center h-[62px]"
                        onClick={() =>
                           toggleDropdown(`assigned-pond-${row.id}`)
                        }
                     >
                        <span
                           className={`flex-1 ${
                              !row.assignedPonds?.length
                                 ? 'text-gray-400'
                                 : 'text-white'
                           }`}
                        >
                           {getSelectedAssignedPondsText(
                              row.assignedPonds || []
                           )}
                        </span>
                        <Icon name="arrow-down" className="fill-white" />
                        {(row.assignedPonds?.length || 0) > 0 && (
                           <span className="flex items-center justify-center w-5 h-5 ml-2 text-xs bg-purple-600 rounded-full">
                              {row.assignedPonds?.length}
                           </span>
                        )}
                     </div>

                     {openDropdowns[`assigned-pond-${row.id}`] && (
                        <div className="absolute mt-1 w-full bg-[#2a2a2a] border border-gray-700 rounded-md z-10 shadow-lg max-h-[300px] overflow-y-auto">
                           <div
                              className="flex items-center px-4 py-2 border-b border-gray-700 cursor-pointer hover:bg-[#333] sticky top-0 bg-[#2a2a2a]"
                              onClick={() =>
                                 handleAssignedPondSelectAll(row.id)
                              }
                           >
                              <CheckBox
                                 checked={
                                    row.assignedPonds?.length ===
                                    pondOptions?.length
                                 }
                                 onChange={() =>
                                    handleAssignedPondSelectAll(row.id)
                                 }
                                 className="mr-3"
                              />
                              <span>Select all</span>
                              <span className="ml-auto text-gray-400">
                                 Total Members
                              </span>
                           </div>

                           <div className="overflow-y-auto">
                              {pondOptions?.map((option) => (
                                 <div
                                    key={option.value}
                                    className="flex items-center px-4 py-2 cursor-pointer hover:bg-[#333]"
                                    onClick={() =>
                                       handleAssignedPondToggle(
                                          row.id,
                                          option.value
                                       )
                                    }
                                 >
                                    <CheckBox
                                       checked={
                                          row.assignedPonds?.includes(
                                             option.value
                                          ) || false
                                       }
                                       onChange={() =>
                                          handleAssignedPondToggle(
                                             row.id,
                                             option.value
                                          )
                                       }
                                       className="mr-3"
                                    />
                                    <span>{option?.label}</span>
                                    <span className="ml-auto text-gray-400">
                                       {option?.count?.toLocaleString()}
                                    </span>
                                 </div>
                              ))}

                              {/* No results message */}
                              {pondOptions?.length === 0 && (
                                 <div className="py-4 text-center text-gray-400">
                                    No ponds available.
                                 </div>
                              )}
                           </div>
                        </div>
                     )}
                  </div>

                  {/* CheckBox for unassigned leads - only show when ponds are selected */}
                  {(row.assignedPonds?.length || 0) > 0 && (
                     <div className="flex items-center mt-3">
                        <CheckBox
                           checked={row.includeOnlyUnassignedPondLeads || false}
                           onChange={() =>
                              handleUnassignedPondLeadsToggle(row.id)
                           }
                           className="mr-3"
                        />
                        <label
                           className="text-sm text-white cursor-pointer"
                           onClick={() =>
                              handleUnassignedPondLeadsToggle(row.id)
                           }
                        >
                           Include only unassigned leads from the selected
                           ponds.
                        </label>
                     </div>
                  )}
               </div>
            )
      }
   }

   // Render the clear button if there are selected items
   const renderClearButton = (row: FilterRow) => {
      let hasSelection = false

      switch (row.type) {
         case 'source':
            hasSelection = (row.sources?.length || 0) > 0
            break
         case 'stages':
            hasSelection = (row.stages?.length || 0) > 0
            break
         case 'tags':
            hasSelection = (row.tags?.length || 0) > 0
            break
         case 'created':
            hasSelection = !!row.startDate
            break
         case 'age':
            hasSelection = !!(row.ageDays && row.ageDays.trim())
            break
         case 'Ponds':
            hasSelection = (row.assignedPonds?.length || 0) > 0
            break
      }

      if (!hasSelection) return null

      return (
         <button
            className="h-16 text-gray-400 hover:text-white"
            onClick={() => {
               if (row.type === 'source') {
                  setFilterRows((prev) =>
                     prev.map((r) =>
                        r.id === row.id ? { ...r, sources: [] } : r
                     )
                  )
               } else if (row.type === 'stages') {
                  setFilterRows((prev) =>
                     prev.map((r) =>
                        r.id === row.id ? { ...r, stages: [] } : r
                     )
                  )
               } else if (row.type === 'tags') {
                  setFilterRows((prev) =>
                     prev.map((r) => (r.id === row.id ? { ...r, tags: [] } : r))
                  )
               } else if (row.type === 'created') {
                  clearDateFilter(row.id)
               } else if (row.type === 'age') {
                  clearAgeFilter(row.id)
               } else if (row.type === 'Ponds') {
                  setFilterRows((prev) =>
                     prev.map((r) =>
                        r.id === row.id
                           ? {
                                ...r,
                                assignedPonds: [],
                                includeOnlyUnassignedPondLeads: false,
                             }
                           : r
                     )
                  )
               }
            }}
         >
            <svg
               className="w-5 h-5"
               fill="none"
               stroke="currentColor"
               viewBox="0 0 24 24"
               xmlns="http://www.w3.org/2000/svg"
            >
               <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
               />
            </svg>
         </button>
      )
   }

   // First, let's add a function to determine which filter types are already in use
   const getAvailableFilters = () => {
      // Get all filter types currently in use
      const usedTypes = filterRows.map((row) => row.type)

      // Check if 'created' or 'age' is already used
      const hasCreatedFilter = usedTypes.includes('created')
      const hasAgeFilter = usedTypes.includes('age')

      // Return only filters that aren't already in use
      return filters.filter((filter) => {
         // If this type is already used, exclude it
         if (usedTypes.includes(filter.value as FilterType)) {
            return false
         }

         // If 'created' is used, exclude 'age'
         if (hasCreatedFilter && filter.value === 'age') {
            return false
         }

         // If 'age' is used, exclude 'created'
         if (hasAgeFilter && filter.value === 'created') {
            return false
         }

         return true
      })
   }

   const [estimatedLeads, setEstimatedLeads] = useState<number>(0)
   const [isLoadingEstimate, setIsLoadingEstimate] = useState(false)

   // Effect to get estimated leads when filter data changes
   useEffect(() => {
      const getEstimate = async () => {
         try {
            setIsLoadingEstimate(true)
            const campaignData = prepareCampaignData()

            // Only call the API if we have some filter data
            const hasFilters =
               campaignData.filters && campaignData.filters.length > 0

            if (hasFilters) {
               const response = await CampaignService.getEstimatedLeads(
                  campaignData
               )
               setEstimatedLeads(response?.count || 0)
            } else {
               setEstimatedLeads(0)
            }
         } catch (error) {
            console.error('Failed to get estimated leads:', error)
            setEstimatedLeads(0)
         } finally {
            setIsLoadingEstimate(false)
         }
      }

      // Debounce the API call to avoid too many requests
      const timeoutId = setTimeout(() => {
         getEstimate()
      }, 500) // 500ms debounce

      return () => clearTimeout(timeoutId)
   }, [filterRows]) // Trigger when filterRows changes

   return (
      <>
         <Modal
            className="p-0 ff-modal !w-[850px]"
            title=""
            open={isAdvanceTargetingCampaignModalOpen}
            onCancel={closeAdvanceTargetingCampaignModal}
            footer={null}
         >
            <div className="px-8 pb-3 mb-5 border-b border-white/20 pt-7">
               <p className="text-[20px] font-medium mb-4 leading-6">
                  {isEditMode ? `Edit Campaign Filters` : 'New Campaign'}
               </p>
            </div>
            {/* Campaign Name Input */}
            <div className="px-8 mb-6">
               <div className="flex items-center justify-between mb-5">
                  <label className="block">Campaign name</label>
                  <label className="block">
                     Estimated Reach:{' '}
                     {isLoadingEstimate ? (
                        <span className="animate-pulse">Loading...</span>
                     ) : (
                        `${estimatedLeads.toLocaleString()} Leads`
                     )}
                  </label>
               </div>

               <input
                  type="text"
                  className={`w-full h-[62px] bg-[#2a2a2a] text-white px-4 py-2 rounded-md focus:outline-none ${
                     campaignNameError
                        ? 'border border-red-500 focus:ring-red-500'
                        : 'focus:ring-2 focus:ring-purple-500'
                  }`}
                  placeholder="Campaign Name"
                  value={campaignName}
                  onChange={handleCampaignNameChange}
               />
               {campaignNameError && (
                  <p className="mt-1 text-sm text-red-500">
                     Campaign name is required
                  </p>
               )}
            </div>
            {/* Target Leads Section */}
            <div className="px-8">
               <p className="mb-2">Define Target Leads</p>
               <p className="mb-4 text-[12px] text-white/70">
                  Tell us who you want to reach – by source, stage, tags, or
                  when they came in, we'll intelligently generate a tailored
                  campaign.
               </p>

               {/* Filter Rows */}
               {filterRows.map((row) => (
                  <div key={row.id} className="flex items-start gap-2 mb-4">
                     {/* Filter Type Dropdown */}
                     <div
                        className="relative"
                        ref={(el) =>
                           (dropdownRefs.current[`type-${row.id}`] = el)
                        }
                     >
                        <div
                           className="flex items-center bg-[#2a2a2a] px-4 py-2 rounded-md cursor-pointer h-[62px] min-w-[180px]"
                           onClick={() => toggleDropdown(`type-${row.id}`)}
                        >
                           {getFilterIcon(row.type)}
                           <span className="mr-auto text-white capitalize">
                              {row.type}
                           </span>
                           <svg
                              className="w-4 h-4 ml-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                           >
                              <path
                                 strokeLinecap="round"
                                 strokeLinejoin="round"
                                 strokeWidth={2}
                                 d="M19 9l-7 7-7-7"
                              />
                           </svg>
                        </div>

                        {openDropdowns[`type-${row.id}`] && (
                           <div className="absolute mt-1 w-full bg-[#2a2a2a] border border-gray-700 rounded-md z-10 shadow-lg">
                              {filters
                                 .filter((filter) => {
                                    // If this is the current row's type, always include it
                                    if (filter.value === row.type) return true

                                    // Otherwise, check if this type is already used in another row
                                    const isTypeUsed = filterRows.some(
                                       (otherRow) =>
                                          otherRow.id !== row.id &&
                                          otherRow.type === filter.value
                                    )

                                    if (isTypeUsed) return false

                                    // Check for mutual exclusivity between 'created' and 'age'
                                    const hasOtherCreatedOrAge =
                                       filterRows.some(
                                          (otherRow) =>
                                             otherRow.id !== row.id &&
                                             (otherRow.type === 'created' ||
                                                otherRow.type === 'age')
                                       )

                                    if (
                                       (filter.value === 'created' ||
                                          filter.value === 'age') &&
                                       hasOtherCreatedOrAge
                                    ) {
                                       return false
                                    }

                                    return true
                                 })
                                 .map((filter) => (
                                    <div
                                       key={filter.value}
                                       className="px-4 py-2 cursor-pointer hover:bg-[#333] text-white flex items-center"
                                       onClick={() =>
                                          changeFilterType(
                                             row.id,
                                             filter.value as FilterType
                                          )
                                       }
                                    >
                                       {getFilterIcon(
                                          filter.value as FilterType
                                       )}
                                       <span>{filter.label}</span>
                                    </div>
                                 ))}
                           </div>
                        )}
                     </div>

                     {/* Filter Control based on type */}
                     {renderFilterControl(row)}
                     {/* Clear selected items button */}
                     {renderClearButton(row)}

                     {/* Remove Filter button (only if more than one filter exists) */}
                     {filterRows.length > 1 && (
                        <button
                           className="h-16 ml-1 text-gray-400 hover:text-white"
                           onClick={() => removeFilterRow(row.id)}
                        >
                           <Icon name="Delete" className="fill-gray-400" />
                        </button>
                     )}
                  </div>
               ))}

               {/* Add Filter Button - show only if there are available filters left */}
               {getAvailableFilters().length > 0 && (
                  <button
                     className="bg-[#2a2a2a] text-white px-4 py-2 rounded-md w-full text-center hover:bg-[#333] mb-6 h-[62px]"
                     onClick={addFilterRow}
                  >
                     Add Filter
                  </button>
               )}
            </div>
            {/* Footer */}

            <div className="flex justify-end gap-3 p-4 mt-6 border-t border-gray-700">
               <button
                  className="h-12 px-4 py-2 font-semibold text-white rounded-md bg-white/5 hover:bg-gray-600 w-44"
                  onClick={closeAdvanceTargetingCampaignModal}
               >
                  Cancel
               </button>

               <Button
                  isLoading={isLoading}
                  className="flex items-center justify-center gap-2 px-4 py-2 !mt-0 font-semibold text-white !rounded-md bg-primary !w-44 hover:bg-purple-700"
                  onClick={async () => {
                     if (!validateCampaignName()) {
                        return
                     }
                     setIsLoading(true)
                     const campaignData = prepareCampaignData()

                     try {
                        let responseActiveLeadSource: any
                        let responseUpdatedLeadSource: any
                        if (isEditMode && activeLeadSourceData?.id) {
                           responseActiveLeadSource =
                              await CampaignService.editCustomActiveLeadSource(
                                 activeLeadSourceData?.id,
                                 campaignData
                              )
                           responseUpdatedLeadSource =
                              await CampaignService.updateCampaignName(
                                 activeLeadSourceData?.id,
                                 campaignName
                              )
                        } else {
                           responseActiveLeadSource =
                              await CampaignService.createCustomActiveLeadSource(
                                 campaignData?.campaignName as string,
                                 campaignData,
                                 'new-lead'
                              )
                        }
                        if (responseUpdatedLeadSource) {
                           message.success('Campaign updated successfully')

                           const newSearchParams = new URLSearchParams(
                              searchParams
                           )
                           newSearchParams.set(
                              'campaignName',
                              responseUpdatedLeadSource?.campaignName
                           )
                           navigate(
                              `${
                                 window.location.pathname
                              }?${newSearchParams.toString()}`,
                              { replace: true }
                           )
                        }
                        if (
                           responseActiveLeadSource &&
                           responseActiveLeadSource.id
                        ) {
                           if (isEditMode) {
                              message.success('Campaign updated successfully')
                           } else {
                              navigate(
                                 `/campaign/${encodeURIComponent(
                                    responseActiveLeadSource.id
                                 )}/phone-number?campaignName=${campaignName}`
                              )
                           }
                        }
                     } catch (e) {
                        message.error(
                           isEditMode
                              ? 'Failed to update campaign'
                              : 'Failed to create campaign'
                        )
                     } finally {
                        closeAdvanceTargetingCampaignModal()
                        setIsLoading(false)
                        mutateActiveLeadSource()
                        mutateActiveLeadSourceDetails()
                     }
                  }}
               >
                  <Icon name="TourIcon" />
                  {isEditMode ? 'Update' : 'Activate'}
               </Button>
            </div>
         </Modal>
      </>
   )
}
